'use client';

import 'BomdisoftComponents/inputs/entry-list/styles.css';
import { Button } from 'BomdisoftComponents/buttons/index';
import { Status } from 'BomdisoftComponents/chips/status';
import { canAddEntry, type EntryListOwn, toListedEntries } from 'BomdisoftComponents/inputs/entry-list/render';
import { TextField } from 'BomdisoftComponents/inputs/field/text';

export type { EntryListOwn, EntryListOwnState } from 'BomdisoftComponents/inputs/entry-list/render';

export type EntryListProps = {
  addAriaLabel: (entry: string) => string;
  addLabel: string;
  entries: readonly string[];
  fieldError?: boolean;
  fieldHelperText?: string;
  fieldLabel: string;
  fieldValue: string;
  isFull?: boolean;
  onAdd: (entry: string) => void;
  onFieldChange: (value: string) => void;
  onRemove: (entry: string) => void;
  own?: EntryListOwn;
  removeAriaLabel: (entry: string) => string;
  removeLabel: string;
  testId?: string;
};

const testIdOf = (testId: string | undefined, part: string) => (testId ? `${testId}-${part}` : undefined);

export const EntryList = ({ addAriaLabel, addLabel, entries, fieldError, fieldHelperText, fieldLabel, fieldValue, isFull = false, onAdd, onFieldChange, onRemove, own, removeAriaLabel, removeLabel, testId }: EntryListProps) => (
  <div className='bomdisoft-entry-list' data-testid={testId}>
    <ul className='bomdisoft-entry-list-items'>
      {own === undefined ? null : (
        <li className='bomdisoft-entry-list-item' data-entry={own.entry} data-own={own.state} data-testid={testIdOf(testId, 'own')}>
          <span className='bomdisoft-entry-list-name'>
            {own.entry} <Status capitalize={false} size='small' text={own.tag} variant='info' />
          </span>
          {own.state === 'listed' ? (
            <Button aria-label={removeAriaLabel(own.entry)} color='error' data-entry-action='remove' data-testid={testIdOf(testId, 'own-remove')} onClick={() => onRemove(own.entry)} size='small'>
              {removeLabel}
            </Button>
          ) : own.state === 'missing' ? (
            <Button aria-label={addAriaLabel(own.entry)} data-entry-action='add' data-testid={testIdOf(testId, 'own-add')} disabled={isFull} onClick={() => onAdd(own.entry)} size='small'>
              {addLabel}
            </Button>
          ) : null}
        </li>
      )}
      {toListedEntries(entries, own).map(entry => (
        <li className='bomdisoft-entry-list-item' data-entry={entry} data-testid={testIdOf(testId, `row-${entry}`)} key={entry}>
          <span className='bomdisoft-entry-list-name'>{entry}</span>
          <Button aria-label={removeAriaLabel(entry)} color='error' data-entry-action='remove' data-testid={testIdOf(testId, `remove-${entry}`)} onClick={() => onRemove(entry)} size='small'>
            {removeLabel}
          </Button>
        </li>
      ))}
    </ul>
    <div className='bomdisoft-entry-list-adder'>
      <TextField data-testid={testIdOf(testId, 'field')} disabled={isFull} error={fieldError} helperText={fieldHelperText} label={fieldLabel} onChange={event => onFieldChange((event.target as HTMLInputElement).value)} value={fieldValue} />
      <Button data-entry-action='add' data-testid={testIdOf(testId, 'add')} disabled={!canAddEntry(entries, fieldValue, isFull)} onClick={() => onAdd(fieldValue.trim())} size='small' variant='outlined'>
        {addLabel}
      </Button>
    </div>
  </div>
);
